import React, { useEffect, useRef } from 'react'
import { useSplineViewer } from '@lib/hooks/useSplineViewer'

// Extend the global JSX namespace to include spline-viewer

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'spline-viewer': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          url?: string
          'events-target'?: string
        },
        HTMLElement
      >
    }
  }
}

export interface SplineViewerProps extends React.HTMLAttributes<HTMLElement> {
  url: string
  viewerId?: string
  // onLoad?: () => void
  // onReady?: () => void
  // onError?: (_error: any) => void
  className?: string
  style?: React.CSSProperties
  eventsTarget?: string
}

export const SplineViewer: React.FC<SplineViewerProps> = ({
  url,
  viewerId,
  // onLoad,
  // onReady,
  // onError,
  className,
  style,
  eventsTarget = 'global',
  ...rest
}) => {
  const viewerRef = useRef<HTMLElement>(null)
  const { setViewerElement, markViewerReady } = useSplineViewer()

  // Register the viewer element with the hook when viewerId is provided
  useEffect(() => {
    if (viewerId) {
      // Set a timeout to ensure the DOM element is fully rendered
      const timeoutId = setTimeout(() => {
        if (viewerRef.current) {
          setViewerElement(viewerId, viewerRef.current)
        }
      }, 0)

      return () => {
        clearTimeout(timeoutId)
        setViewerElement(viewerId, null)
      }
    }
  }, [viewerId, setViewerElement])

  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer) {
      return
    }

    // Mark this viewer ready on native 'load' event (fires when scene finishes loading)
    if (viewerId) {
      const onLoad = () => {
        markViewerReady(viewerId)
      }
      viewer.addEventListener('load', onLoad)

      // If Spline already attached and loaded before we subscribed, mark ready now
      const anyViewer = viewer as unknown as { spline?: unknown }
      if (anyViewer.spline) {
        markViewerReady(viewerId)
      } else {
        // Fallback: Poll briefly for the 'spline' property to appear
        const start = Date.now()
        const poll = () => {
          const v = viewerRef.current as unknown as { spline?: unknown } | null
          if (!v) {
            return
          }
          if (v.spline) {
            markViewerReady(viewerId)
            return
          }
          if (Date.now() - start < 5000) {
            requestAnimationFrame(poll)
          }
        }
        requestAnimationFrame(poll)
      }

      return () => {
        viewer.removeEventListener('load', onLoad)
      }
    }
  }, [url, viewerId, markViewerReady])

  return (
    <spline-viewer
      ref={viewerRef}
      url={url}
      events-target={eventsTarget}
      className={className}
      style={style}
      {...rest}
    />
  )
}
